import { Facet } from "@codemirror/state";
import { ApiError } from "@/api/client";
import type {
  PlaygroundTestRequest,
  PlaygroundTestResponse,
  ScriptDiagnostic,
} from "@/types/api";

// The transform linter's second opinion (TTTL-225): Rhai resolves variables
// and function calls at run time, so a script that parses clean can still
// fail on `let x = wer` or `ererrr(data)`. Until the engine's validate
// learns strict variables and a function walk, the linter dry-runs the
// script against the Studio's staged input document through the same
// endpoint the Run button uses (POST /transform/run) and lifts the
// engine's runtime error, with its `(line L, position C)`, into a
// diagnostic. Only the branches the input document exercises are covered.

export type TestScript = (
  request: PlaygroundTestRequest,
) => Promise<PlaygroundTestResponse>;

/** The staged input document the dry run feeds the script — held in
 *  editor state so a change to it re-lints the script on its own. */
export const dryRunPayload = Facet.define<string, string>({
  combine: (values) => values[0] ?? "",
});

/** The dry run needs JSON to run against; a blank or broken input
 *  document means no runtime verdict (the parse verdict stands alone). */
export function isRunnablePayload(payload: string): boolean {
  if (payload.trim() === "") return false;
  try {
    JSON.parse(payload);
    return true;
  } catch {
    return false;
  }
}

// The engine's run failure reads "stage N: UDF script execution failed:
// <rhai error> (line L, position C). path: <file>"; a call chain nests
// as "in call to 'f' (line, position): <inner> (line, position)" with the
// innermost error LAST.
const STAGE_PREFIX = /^stage \d+: /;
const EXECUTION_PREFIX = /^UDF script execution failed: /;
const PATH_SUFFIX = /\. path: [^\n]*$/;
const POSITION = /\s*\(line (\d+), position (\d+)\)/g;

/** The engine's runtime failure message as a diagnostic at the innermost
 *  position it names; a message without one lands on line 1 (where the
 *  engine's own "missing transform" verdict goes). */
export function runtimeDiagnostic(message: string): ScriptDiagnostic {
  const text = message
    .replace(STAGE_PREFIX, "")
    .replace(EXECUTION_PREFIX, "")
    .replace(PATH_SUFFIX, "");
  const positions = [...text.matchAll(POSITION)];
  const innermost = positions[positions.length - 1];
  const line = innermost ? Number(innermost[1]) : 1;
  const column = innermost ? Number(innermost[2]) : 1;
  return {
    line,
    column,
    end_line: line,
    end_column: column,
    message: text.replace(POSITION, ""),
    severity: "error",
  };
}

/** Runs `script` alone against `payload` and returns the engine's runtime
 *  verdict: no diagnostics for a clean run, one for a failed stage. The
 *  engine answers a failed run with a 4xx and the message in the body
 *  (the API client throws it as an ApiError); anything else — an engine
 *  outage, a network failure — propagates, so the caller can keep the
 *  parse verdict instead of reading silence as clean. */
export async function dryRunScript(
  test: TestScript,
  script: string,
  payload: string,
): Promise<ScriptDiagnostic[]> {
  let response: PlaygroundTestResponse;
  try {
    response = await test({ scripts: [script], payload });
  } catch (error) {
    if (error instanceof ApiError && error.status < 500)
      return [runtimeDiagnostic(error.message)];
    throw error;
  }
  if (response.item?.failed_stage !== undefined)
    return [runtimeDiagnostic(response.message)];
  return [];
}
